import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import axios from 'axios';
import { Lock, Star, Crown, Check, X, Loader2, Rocket, ArrowRight } from 'lucide-react';
import { foundingJustSoldOut } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURES = [
  'Neomezený přístup ke všemu obsahu',
  'Exkluzivní epizody od tvůrců',
  'HD video i audio kvalita',
  'Bez reklam',
  'Podpora oblíbených tvůrců',
];

/**
 * PaywallModal
 *
 * Props:
 *   open         {boolean}   – whether modal is visible
 *   onClose      {function}  – called when user dismisses
 *   episodeTitle {string}    – name of the locked episode (optional)
 */
export default function PaywallModal({ open, onClose, episodeTitle }) {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [foundingStatus, setFoundingStatus] = useState(null);
  const [billingInfo, setBillingInfo] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoadingStatus(true);

    const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};

    Promise.all([
      axios.get(`${API}/subscriptions/founding-status`, { headers }),
      accessToken
        ? axios.get(`${API}/billing/info`, { headers })
        : Promise.resolve(null),
    ])
      .then(([fsRes, biRes]) => {
        setFoundingStatus(fsRes.data);
        if (biRes) setBillingInfo(biRes.data);
      })
      .catch(() => {})
      .finally(() => setLoadingStatus(false));
  }, [open, accessToken]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  const foundingAvailable = foundingStatus?.remaining > 0;
  const justSoldOut = foundingJustSoldOut(foundingStatus);
  const monthlyPrice = foundingAvailable ? 199 : 299;
  const monthlyDailyPrice = (monthlyPrice / 30).toFixed(2).replace('.', ',');
  const trialDailyPrice = (99 / 7).toFixed(2).replace('.', ',');

  // Can user still use the weekly trial?
  const canTrial = billingInfo ? billingInfo.can_use_weekly_trial : true;

  const goToSubscribe = (anchor) => {
    onClose();
    navigate(anchor ? `/subscribe#${anchor}` : '/subscribe');
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">

        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors z-10"
          aria-label="Zavřít"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="px-6 pt-7 pb-3 text-center">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
            <Lock className="w-7 h-7 text-primary" />
          </div>
          <h2 className="text-xl font-bold text-foreground mb-1">Prémiový obsah</h2>
          {episodeTitle && (
            <p className="text-sm text-muted-foreground truncate max-w-[280px] mx-auto">{episodeTitle}</p>
          )}
          <p className="text-muted-foreground text-sm mt-1">
            Aktivujte OneSub Pro a získejte přístup ke všemu obsahu.
          </p>
        </div>

        <div className="px-6 pb-6 space-y-3">

          {loadingStatus ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : (
            <>
              {/* ── Trial CTA (highlighted, shown only if eligible) ── */}
              {canTrial && (
                <div className="relative rounded-xl border-2 border-primary bg-primary/5 p-4">
                  <div className="absolute -top-3 left-4">
                    <Badge className="bg-primary text-white text-[10px] px-2 py-0.5 font-semibold">
                      <Rocket className="w-3 h-3 mr-1" />
                      Doporučujeme
                    </Badge>
                  </div>
                  <div className="flex items-end justify-between mt-1">
                    <div>
                      <p className="text-xs text-muted-foreground mb-0.5">7denní trial</p>
                      <div>
                        <span className="text-3xl font-bold text-primary">99</span>
                        <span className="text-muted-foreground text-sm ml-1">Kč</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        ≈ {trialDailyPrice} Kč/den
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[11px] text-muted-foreground">Pak automaticky</p>
                      <p className="text-sm font-semibold text-foreground">{monthlyPrice} Kč/měs</p>
                      <p className="text-[10px] text-muted-foreground">· zrušit lze kdykoliv</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => goToSubscribe('trial')}
                    className="w-full mt-3 bg-primary hover:bg-primary/90 h-10"
                  >
                    <Rocket className="w-4 h-4 mr-2" />
                    Zkusit 7 dní za 99 Kč
                  </Button>
                </div>
              )}

              {/* ── Monthly pricing ── */}
              <div className={`rounded-xl border p-4 ${canTrial ? 'border-border bg-secondary/20' : 'border-2 border-primary/60 bg-primary/5'}`}>
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">
                      {foundingAvailable ? 'Zakládající člen' : 'Měsíční předplatné'}
                    </p>
                    {justSoldOut && (
                      <p className="text-[11px] text-muted-foreground mb-0.5">
                        Zakládající místa za 199 Kč právě vyprodána
                      </p>
                    )}
                    <div>
                      <span className={`text-3xl font-bold ${foundingAvailable ? 'text-yellow-500' : 'text-foreground'}`}>
                        {monthlyPrice}
                      </span>
                      <span className="text-muted-foreground text-sm ml-1">Kč/měs</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">≈ {monthlyDailyPrice} Kč/den</p>
                  </div>
                  {foundingAvailable && (
                    <div className="text-right">
                      <Badge className="bg-yellow-500/15 text-yellow-500 border-yellow-500/30 text-[10px]">
                        <Star className="w-2.5 h-2.5 mr-0.5 fill-yellow-500" />
                        Cena na 12 měsíců
                      </Badge>
                      {foundingStatus.remaining < foundingStatus.total && (
                        <p className="text-[10px] text-muted-foreground mt-1">
                          Zbývá {foundingStatus.remaining} míst
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-1.5 pt-1">
                {FEATURES.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Check className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* Main CTA */}
          <Button
            onClick={() => goToSubscribe()}
            className={`w-full py-5 text-base font-semibold ${canTrial ? 'bg-secondary text-foreground hover:bg-secondary/80 border border-border' : 'bg-primary text-white hover:bg-primary/90'}`}
            variant="ghost"
          >
            {canTrial ? (
              <>
                <ArrowRight className="w-4 h-4 mr-2" />
                {foundingAvailable ? `Rovnou za ${monthlyPrice} Kč/měs` : 'Zobrazit všechny možnosti'}
              </>
            ) : (
              <>
                <Crown className="w-4 h-4 mr-2" />
                {`Aktivovat za ${monthlyPrice} Kč/měsíc`}
              </>
            )}
          </Button>

          <button
            onClick={onClose}
            className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
          >
            Možná později
          </button>
        </div>
      </div>
    </div>
  );
}
